import React, { useState, useContext } from "react";
import { gql, useMutation } from "@apollo/client";
import {
    Offcanvas,
    Form,
    Button,
    Spinner as CommonSpinner,
} from "react-bootstrap";
import StarekContext from "../../context/StarekContext";
import "bootstrap/dist/css/bootstrap.min.css";

const UPDATE_DEVICE_PROFILE = gql`
    mutation UpdateDeviceProfile(
        $id: String!
        $data: DeviceProfileUpdateInput!
    ) {
        updateOneDeviceProfile(where: { id: $id }, data: $data) {
            id
            name
            description
        }
    }
`;

const CREATE_DEVICE_PROFILE = gql`
    mutation CreateDeviceProfile($data: DeviceProfileCreateInput!) {
        createOneDeviceProfile(data: $data) {
            id
            name
            description
        }
    }
`;

const AddDeviceProfileSidebar = ({ profile = null, onClose = () => {} }) => {
    const starekContext = useContext(StarekContext);
    const [formData, setFormData] = useState({
        name: profile?.name || "",
        description: profile?.description || "",
    });

    const isEditing = !!profile;

    const [updateDeviceProfile, { loading: updateLoading }] = useMutation(
        UPDATE_DEVICE_PROFILE,
        {
            onCompleted: () => {
                starekContext.showInfo("Device Profile updated successfully");
                onClose();
            },
            onError: (error) => {
                starekContext.showError(
                    error.message || "Failed to update Device Profile"
                );
            },
        }
    );

    const [createDeviceProfile, { loading: createLoading }] = useMutation(
        CREATE_DEVICE_PROFILE,
        {
            onCompleted: () => {
                starekContext.showInfo("Device Profile created successfully");
                onClose();
            },
            onError: (error) => {
                starekContext.showError(
                    error.message || "Failed to create Device Profile"
                );
            },
        }
    );

    const loading = updateLoading || createLoading;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            starekContext.showError("Device Profile name is required");
            return;
        }

        try {
            if (isEditing) {
                await updateDeviceProfile({
                    variables: {
                        id: profile.id,
                        data: {
                            name: formData.name,
                            description: formData.description,
                        },
                    },
                });
            } else {
                await createDeviceProfile({
                    variables: {
                        data: {
                            name: formData.name,
                            description: formData.description,
                            configuration: {},
                            plant: {
                                connect: {
                                    id: starekContext.lastUsedPlant.id,
                                },
                            },
                            created_by: {
                                connect: {
                                    id: parseInt(starekContext.user.id),
                                },
                            },
                            updated_by: {
                                connect: {
                                    id: parseInt(starekContext.user.id),
                                },
                            },
                        },
                    },
                });
            }
        } catch (error) {
            console.error("Save failed:", error);
        }
    };

    return (
        <Offcanvas
            show={true}
            onHide={onClose}
            placement="end"
            backdrop="static"
            keyboard={false}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {isEditing ? "Edit Device Profile" : "Add Device Profile"}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="d-flex flex-column">
                <Form
                    onSubmit={handleSubmit}
                    className="flex-grow-1 d-flex flex-column"
                >
                    <Form.Group className="mb-3">
                        <Form.Label>Profile Name *</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter profile name"
                            required
                            disabled={loading}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter profile description"
                            disabled={loading}
                        />
                    </Form.Group>
                </Form>
            </Offcanvas.Body>

            {/* Footer buttons */}
            <div
                className="d-flex justify-content-end align-items-center px-3 pb-3"
                style={{ gap: 8 }}
            >
                <Button
                    variant="outline-secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Discard
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="d-flex align-items-center justify-content-center gap-2"
                >
                    {loading && (
                        <CommonSpinner
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />
                    )}
                    {loading ? "Saving..." : "Save"}
                </Button>
            </div>
        </Offcanvas>
    );
};

export default AddDeviceProfileSidebar;
